import React, { useState } from 'react';
import { Upload, FileText, Globe, Loader2 } from 'lucide-react';
import { aiMenuImportService } from '../../services/menuService';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';

interface AIMenuImportProps {
  menuId: string;
  onImportComplete?: () => void;
}

const AIMenuImport: React.FC<AIMenuImportProps> = ({ menuId, onImportComplete }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'file' | 'text' | 'url'>('file');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const [menuText, setMenuText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; stats?: any } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      
      if (!validTypes.includes(selectedFile.type)) {
        setError(t('ai_menu_import.messages.invalid_file_type'));
        return;
      }
      
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleFileImport = async () => {
    if (!file) {
      setError(t('ai_menu_import.messages.no_file_selected'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await aiMenuImportService.importFromFile(menuId, file);
      
      if (response.success) {
        setResult({
          success: true,
          message: response.message,
          stats: response.stats
        });
        const catCount = response.stats?.totalCategories || response.stats?.categories || 0;
        const itemCount = response.stats?.totalItems || response.stats?.items || 0;
        toast.success(
          t('ai_menu_import.results.success') + (response.stats ? ` (${catCount} categorie, ${itemCount} piatti)` : ''),
          { duration: 5000 }
        );
        if (onImportComplete) onImportComplete();
      } else {
        const errMsg = response.error || response.message || t('ai_menu_import.messages.import_error');
        setError(errMsg);
        toast.error(errMsg);
      }
    } catch (err) {
      setError(t('ai_menu_import.messages.generic_error'));
      toast.error(t('ai_menu_import.messages.generic_error'));
    } finally {
      setLoading(false);
    }
  };



  const handleTextInputImport = async () => {
    if (!menuText.trim()) {
      setError(t('ai_menu_import.messages.no_text_entered'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await aiMenuImportService.importFromText(menuId, menuText);
      
      if (response.success) {
        setResult({
          success: true,
          message: response.message,
          stats: response.stats
        });
        const catCount = response.stats?.totalCategories || response.stats?.categories || 0;
        const itemCount = response.stats?.totalItems || response.stats?.items || 0;
        toast.success(
          t('ai_menu_import.results.success') + (response.stats ? ` (${catCount} categorie, ${itemCount} piatti)` : ''),
          { duration: 5000 }
        );
        if (onImportComplete) onImportComplete();
      } else {
        const errMsg = response.error || response.message || t('ai_menu_import.messages.import_error');
        setError(errMsg);
        toast.error(errMsg);
      }
    } catch (err) {
      setError(t('ai_menu_import.messages.generic_error'));
      toast.error(t('ai_menu_import.messages.generic_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUrlImport = async () => {
    if (!url.trim()) {
      setError(t('ai_menu_import.messages.no_url_entered'));
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError(t('ai_menu_import.messages.invalid_url_format'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await aiMenuImportService.importFromURL(menuId, url);
      
      if (response.success) {
        setResult({
          success: true,
          message: response.message,
          stats: response.stats
        });
        const catCount = response.stats?.totalCategories || response.stats?.categories || 0;
        const itemCount = response.stats?.totalItems || response.stats?.items || 0;
        toast.success(
          t('ai_menu_import.results.success') + (response.stats ? ` (${catCount} categorie, ${itemCount} piatti)` : ''),
          { duration: 5000 }
        );
        if (onImportComplete) onImportComplete();
      } else {
        const errMsg = response.error || response.message || t('ai_menu_import.messages.import_error');
        setError(errMsg);
        toast.error(errMsg);
      }
    } catch (err) {
      setError(t('ai_menu_import.messages.generic_error'));
      toast.error(t('ai_menu_import.messages.generic_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'file'
              ? 'text-[#0A0C0B] border-b-2 border-[#0A0C0B]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('file')}
        >
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            {t('ai_menu_import.tabs.file')}
          </div>
        </button>

        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'text'
              ? 'text-[#0A0C0B] border-b-2 border-[#0A0C0B]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('text')}
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t('ai_menu_import.tabs.text')}
          </div>
        </button>

        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'url'
              ? 'text-[#0A0C0B] border-b-2 border-[#0A0C0B]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('url')}
        >
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            {t('ai_menu_import.tabs.url')}
          </div>
        </button>
      </div>

      {activeTab === 'file' && (
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#7BD5B5] transition-colors">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">{t('ai_menu_import.file_tab.upload_prompt')}</p>
            <p className="text-sm text-gray-500 mb-4">{t('ai_menu_import.file_tab.supported_formats')}</p>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="inline-flex items-center px-4 py-2 bg-[#7BD5B5] text-[#0A0C0B] rounded-lg hover:bg-[#6bc4a5] transition-colors cursor-pointer"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('ai_menu_import.file_tab.choose_file')}
            </label>
            {file && (
              <p className="mt-2 text-sm text-gray-600">
                {t('ai_menu_import.file_tab.selected_file', { 
                  filename: file.name, 
                  size: (file.size / 1024 / 1024).toFixed(2) 
                })}
              </p>
            )}
          </div>

          <button
            onClick={handleFileImport}
            disabled={loading || !file}
            className="w-full py-3 bg-[#0A0C0B] text-white rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('ai_menu_import.messages.importing')}
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                {t('ai_menu_import.file_tab.import_button')}
              </>
            )}
          </button>
        </div>
      )}



      {activeTab === 'text' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('ai_menu_import.text_tab.label')}
            </label>
            <textarea
              value={menuText}
              onChange={(e) => setMenuText(e.target.value)}
              placeholder={t('ai_menu_import.text_tab.placeholder')}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-[#7BD5B5] font-mono text-sm"
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('ai_menu_import.text_tab.instructions')}
              {t('ai_menu_import.text_tab.format_tip')}
            </p>
            <div className="mt-2 text-xs text-gray-400 bg-gray-50 p-2 rounded">
              <strong>{t('ai_menu_import.text_tab.tip')}</strong>
            </div>
          </div>

          <button
            onClick={handleTextInputImport}
            disabled={loading || !menuText.trim()}
            className="w-full py-3 bg-[#0A0C0B] text-white rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('ai_menu_import.messages.analyzing')}
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                {t('ai_menu_import.text_tab.import_button')}
              </>
            )}
          </button>
        </div>
      )}

      {activeTab === 'url' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('ai_menu_import.url_tab.label')}
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('ai_menu_import.url_tab.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-[#7BD5B5]"
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('ai_menu_import.url_tab.instructions')}
            </p>
          </div>

          <button
            onClick={handleUrlImport}
            disabled={loading || !url.trim()}
            className="w-full py-3 bg-[#0A0C0B] text-white rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('ai_menu_import.messages.importing')}
              </>
            ) : (
              <>
                <Globe className="w-4 h-4" />
                {t('ai_menu_import.url_tab.import_button')}
              </>
            )}
          </button>
        </div>
      )}

    

      {result && (
        <div className={`mt-4 p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <p className={result.success ? 'text-green-700' : 'text-red-700'}>
            {result.success 
              ? t('ai_menu_import.results.success') 
              : t('ai_menu_import.results.error')}
          </p>
          {result.stats && (
            <div className="mt-2 text-sm">
              <p>{t('ai_menu_import.results.categories', { count: result.stats.totalCategories || result.stats.categories || 0 })}</p>
              <p>{t('ai_menu_import.results.items', { count: result.stats.totalItems || result.stats.items || 0 })}</p>
            </div>
          )}
        </div>
      )}

   
    </div>
  );
};

// AI Menu Import Component
export default AIMenuImport;